<template>
  <div class="search-area">
    <el-row v-for="(row, ri) in visibleRows" :key="ri" :gutter="24">
      <el-col v-for="col in row" :key="col.key" :span="6">
        <div class="search-area-div" :style="{ width: labelWidth + 'px' }">{{ col.label }}</div>
        <el-input
          v-if="col.type === 'input'"
          v-bind="col.props"
          size="mini"
          v-model="query[col.key]"
          :placeholder="col.placeholder || '请输入'"
          clearable
          class="ipt-sty"
        ></el-input>
        <el-select
          v-else-if="col.type === 'select'"
          v-bind="col.props"
          size="mini"
          v-model="query[col.key]"
          :multiple="col.multiple === true"
          :placeholder="col.placeholder || '请选择'"
          clearable
          class="ipt-sty"
        >
          <el-option
            v-for="opt in col.options || []"
            :key="opt[col.optValue || 'value']"
            :label="opt[col.optLabel || 'label']"
            :value="opt[col.optValue || 'value']"
          ></el-option>
        </el-select>
        <el-date-picker
          v-else-if="col.type === 'date' || col.type === 'daterange'"
          v-bind="col.props"
          size="mini"
          v-model="query[col.key]"
          :type="col.type"
          :value-format="col.valueFormat || 'yyyy-MM-dd'"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          :placeholder="col.placeholder || '请选择'"
          clearable
          class="ipt-sty"
          @change="onDateChange(col, $event)"
        ></el-date-picker>
        <!-- 复杂控件（cascader/树选择等）走具名插槽，插槽内容自带 size="mini" class="ipt-sty" -->
        <slot v-else :name="col.key"></slot>
      </el-col>
      <el-col v-if="ri === 0" :span="6" class="search-btn-box">
        <el-button
          v-if="hasMore"
          size="mini"
          type="text"
          @click="showMore = !showMore"
          :icon="showMore ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
        >
          {{ showMore ? '收起筛选' : '更多筛选' }}
        </el-button>
        <el-button size="mini" type="info" @click="$emit('search')">搜索</el-button>
        <el-button size="mini" type="danger" @click="$emit('reset')">清空</el-button>
      </el-col>
    </el-row>
  </div>
</template>

<script>
/**
 * 列表页搜索区通用组件（样式基准：views/dataManagement/productInformation.vue）
 *
 * 用法：
 *   <search-bar v-model="query" :conditions="conditions"
 *     @search="search" @reset="reset" @height-change="recalcLayout">
 *     <template #someKey><el-cascader size="mini" class="ipt-sty" .../></template>
 *   </search-bar>
 *
 * conditions 配置项：
 *   key          绑定 query 的字段名（必填）
 *   label        条件名，组件统一渲染（无冒号、定宽靠右）
 *   type         input | select | date | daterange | slot；slot 时用 #key 具名插槽放自定义控件
 *   options      select 的选项数组；optLabel/optValue 指定取值字段（默认 label/value）
 *   multiple     select 多选（值为数组）
 *   valueFormat  date/daterange 的值格式，默认 yyyy-MM-dd（要时分秒传 yyyy-MM-dd HH:mm:ss）
 *   startKey/endKey  daterange 选定后自动把区间首尾写入这两个 query 字段，
 *                   后端要 startTime/endTime 两个参数时用，免页面手写 @change 拆分
 *   more: true   放入"更多筛选"折叠行
 *   placeholder  可省略，input 默认"请输入"、select/date 默认"请选择"
 *   props        透传给控件的属性（filterable/maxlength/collapse-tags 等）；
 *               组件内已显式声明的属性（size/v-model 等）不会被覆盖
 *
 * 低频控件（cascader、input-number、远程搜索 select 等）统一走 slot，不为它们加配置型。
 *
 * 注意：条件 key 必须在 query 对象中预先声明（Vue 2 响应式限制，
 * 运行期给对象补新字段不会触发视图更新；reset 整体替换对象不受影响）。
 *
 * 高度联动：组件内部用 ResizeObserver 监听自身高度（展开/收起、条件增删），
 * 变化时 emit height-change；页面在回调里按内容盒顶部锚点重算表格高度，
 * 窗口缩放由页面自行监听 resize 后调用同一方法，页面里不再写高度魔法数。
 */
export default {
  name: "SearchBar",
  props: {
    // 查询条件对象（v-model 绑定；字段原地更新，页面 reset 整体替换对象即可生效）
    value: { type: Object, required: true },
    // 条件配置数组
    conditions: { type: Array, required: true },
    // 条件名列宽，默认 100（7 个汉字内不换行）
    labelWidth: { type: Number, default: 100 },
  },
  data() {
    return {
      showMore: false,
    };
  },
  computed: {
    // prop 别名，模板内 v-model 原地改字段，避免直接引用 prop 触发 lint
    query() {
      return this.value;
    },
    hasMore() {
      return this.conditions.some((col) => col.more);
    },
    // 行拆分：首行最多 3 个条件（第 4 列固定给按钮组），其余每行 4 个；more 条件单独折叠行
    visibleRows() {
      const main = this.conditions.filter((col) => !col.more);
      const rows = [main.slice(0, 3)];
      for (let i = 3; i < main.length; i += 4) rows.push(main.slice(i, i + 4));
      if (this.showMore) {
        const more = this.conditions.filter((col) => col.more);
        for (let i = 0; i < more.length; i += 4) rows.push(more.slice(i, i + 4));
      }
      return rows.filter((row) => row.length);
    },
  },
  methods: {
    // daterange 配置了 startKey/endKey 时，选定/清空后自动把区间首尾写进这两个 query 字段
    onDateChange(col, val) {
      if (col.type !== "daterange" || !col.startKey || !col.endKey) return;
      const range = val || [];
      this.$set(this.query, col.startKey, range[0] || "");
      this.$set(this.query, col.endKey, range[1] || "");
    },
  },
  mounted() {
    // 自身高度变化（更多筛选展开/收起等）时通知页面重算表格高度
    if (typeof ResizeObserver !== "undefined") {
      this._ro = new ResizeObserver(() => this.$emit("height-change"));
      this._ro.observe(this.$el);
    }
  },
  beforeDestroy() {
    if (this._ro) this._ro.disconnect();
  },
};
</script>

<style scoped>
/* label 定宽靠右对齐（用户 09-10 指示，右对齐后与控件留 8px 间距），el-col 变 flex 使 label 与控件同行 */
.search-area-div {
  text-align: right;
  padding-right: 8px;
  box-sizing: border-box;
  flex-shrink: 0;
}
/deep/.el-col-6 {
  display: flex;
}
/* 展开更多筛选后行与行上下间隔 8px（用户 09-10 指示） */
/deep/.el-row + .el-row {
  margin-top: 8px;
}
</style>
